import i18next from 'i18next';
import dayjs from 'dayjs';
import { Button, Spinner } from 'react-bootstrap';
import { FaExternalLinkAlt, FaRegTrashAlt } from 'react-icons/fa';
import { formatCurrency } from 'src/components/CarConfiguratorMotornet/common';
import type { SavedConfigurationRow } from 'src/components/CarConfiguratorMotornet/savedConfigurations/types';

type Row = { row: { original: SavedConfigurationRow } };

export const createColumnsData = (
	onOpen: (item: SavedConfigurationRow) => void,
	onDelete: (item: SavedConfigurationRow) => void,
	busyId: string | null
) => [
	{
		width: 150,
		accessor: 'code',
		id: 'code',
		Header: i18next.t('quotation_code', { defaultValue: 'Codice preventivo' }),
		disableSortBy: true,
		Cell: ({ value }: { value: string | null }) => value || '—',
	},
	{
		width: 140,
		accessor: 'createdAt',
		id: 'createdAt',
		Header: i18next.t('creation_date', { defaultValue: 'Data' }),
		disableSortBy: true,
		Cell: ({ value }: { value: string }) =>
			value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—',
	},
	{
		width: 130,
		accessor: 'listing.makeLabel',
		id: 'makeLabel',
		Header: i18next.t('brand'),
		disableSortBy: true,
		Cell: ({ value }: { value: string }) => value || '—',
	},
	{
		width: 130,
		accessor: 'listing.modelLabel',
		id: 'modelLabel',
		Header: i18next.t('model'),
		disableSortBy: true,
		Cell: ({ value }: { value: string }) => value || '—',
	},
	{
		width: 220,
		accessor: 'listing.trimLabel',
		id: 'trimLabel',
		Header: i18next.t('car_setup'),
		disableSortBy: true,
		Cell: ({ value }: { value: string | null }) => value || '—',
	},
	{
		width: 160,
		id: 'vehicleType',
		Header: i18next.t('vehicle_type', { defaultValue: 'Tipo' }),
		disableSortBy: true,
		Cell: ({ row }: Row) => (
			<div className="d-flex flex-wrap gap-1">
				<span
					className={`badge ${
						row.original.vehicleType === 'stock'
							? 'bg-info'
							: 'bg-light text-dark'
					}`}
				>
					{row.original.vehicleType === 'stock'
						? i18next.t('car_configurator_saved_vehicle_type_stock', {
								defaultValue: 'Pronta consegna',
						  })
						: i18next.t('car_configurator_saved_vehicle_type_new', {
								defaultValue: 'Nuovo',
						  })}
				</span>
				{!row.original.available && (
					<span className="badge bg-warning text-dark">
						{i18next.t('car_configurator_saved_unavailable_badge', {
							defaultValue: 'Non disponibile',
						})}
					</span>
				)}
			</div>
		),
	},
	{
		width: 130,
		accessor: 'pricesAtSave.totalNoVat',
		id: 'totalNoVat',
		Header: i18next.t('total_price', { defaultValue: 'Totale' }),
		disableSortBy: true,
		Cell: ({ value }: { value: number | null }) =>
			value ? formatCurrency(value) : '—',
	},
	{
		width: 100,
		id: 'actions',
		Header: '',
		disableSortBy: true,
		Cell: ({ row }: Row) => {
			const isBusy = busyId === row.original.id;
			const openLabel = i18next.t('open', { defaultValue: 'Apri' });
			const deleteLabel = i18next.t('car_configurator_saved_delete', {
				defaultValue: 'Elimina',
			});

			return (
				<div className="d-flex align-items-center gap-3">
					<Button
						variant="link"
						className="p-0"
						aria-label={openLabel}
						title={openLabel}
						disabled={isBusy}
						onClick={() => onOpen(row.original)}
					>
						{isBusy ? (
							<Spinner animation="border" size="sm" />
						) : (
							<FaExternalLinkAlt size={16} />
						)}
					</Button>
					<Button
						variant="link"
						className="p-0 text-danger"
						aria-label={deleteLabel}
						title={deleteLabel}
						disabled={isBusy}
						onClick={() => onDelete(row.original)}
					>
						<FaRegTrashAlt size={16} />
					</Button>
				</div>
			);
		},
	},
];
